import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Plus, Pencil, Trash2, Eye, BookOpen, Loader2, X, Save, ArrowLeft, Upload, Link2, Tag, ExternalLink, Bot, Sparkles, Clock, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Travel Guide', 'Art & Culture', 'Itineraries', 'Hidden Gems', 'Travel Tips', 'Food & Wine', 'History', 'Architecture'];

export default function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [editing, setEditing] = useState(null);
    const [cities, setCities] = useState([]);
    const [attractions, setAttractions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('posts');
    const [aiSettings, setAiSettings] = useState(null);
    const [contentTypes, setContentTypes] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [genType, setGenType] = useState('');

    const emptyPost = {
        title: '', slug: '', excerpt: '', content: '', cover_image: '',
        category: 'Travel Guide', tags: [], city_ids: [], attraction_ids: [],
        author_name: 'Voxoora Travel Team', status: 'draft', is_featured: false,
        meta_title: '', meta_description: ''
    };
    const [form, setForm] = useState(emptyPost);
    const [tagInput, setTagInput] = useState('');

    const fetchPosts = useCallback(async () => {
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const res = await axios.get(`${API}/api/admin/blog`, { params, withCredentials: true });
            setPosts(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [filter]);

    const fetchRefs = useCallback(async () => {
        try {
            const [c, a] = await Promise.all([
                axios.get(`${API}/api/admin/cities`, { withCredentials: true }),
                axios.get(`${API}/api/admin/attractions`, { withCredentials: true })
            ]);
            setCities(Array.isArray(c.data) ? c.data : c.data.cities || []);
            const attrs = Array.isArray(a.data) ? a.data : a.data.attractions || [];
            setAttractions(attrs.filter(x => !x.assigned_operator_id));
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { fetchPosts(); }, [fetchPosts]);
    useEffect(() => { fetchRefs(); }, [fetchRefs]);
    useEffect(() => { fetchAiSettings(); }, []);

    const fetchAiSettings = async () => {
        try {
            const [sRes, ctRes] = await Promise.all([
                axios.get(`${API}/api/admin/auto-blog/settings`, { withCredentials: true }),
                axios.get(`${API}/api/admin/auto-blog/content-types`, { withCredentials: true })
            ]);
            setAiSettings(sRes.data);
            setContentTypes(ctRes.data);
        } catch (e) { console.error(e); }
    };

    const updateAiSetting = async (updates) => {
        try {
            const res = await axios.put(`${API}/api/admin/auto-blog/settings`, updates, { withCredentials: true });
            setAiSettings(res.data);
            toast.success('Settings updated');
        } catch (e) { toast.error('Failed to update'); }
    };

    const generateNow = async () => {
        setGenerating(true);
        try {
            const res = await axios.post(`${API}/api/admin/auto-blog/generate-now`, { content_type: genType || undefined }, { withCredentials: true });
            toast.success(`Generated: "${res.data.title}" (${res.data.status})`);
            fetchPosts();
            fetchAiSettings();
        } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
        finally { setGenerating(false); }
    };

    const openCreate = () => { setForm(emptyPost); setEditing('new'); };
    const openEdit = async (post) => {
        try {
            const res = await axios.get(`${API}/api/admin/blog/${post.id}`, { withCredentials: true });
            setForm(res.data);
            setEditing(post.id);
        } catch { toast.error('Failed to load post'); }
    };

    const handleSave = async () => {
        if (!form.title.trim()) { toast.error('Title is required'); return; }
        setSaving(true);
        try {
            if (editing === 'new') {
                await axios.post(`${API}/api/admin/blog`, form, { withCredentials: true });
                toast.success('Post created');
            } else {
                await axios.put(`${API}/api/admin/blog/${editing}`, form, { withCredentials: true });
                toast.success('Post updated');
            }
            setEditing(null);
            fetchPosts();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this blog post?')) return;
        try {
            await axios.delete(`${API}/api/admin/blog/${id}`, { withCredentials: true });
            toast.success('Deleted');
            fetchPosts();
        } catch { toast.error('Failed to delete'); }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await axios.post(`${API}/api/files/upload`, fd, { withCredentials: true });
            setForm(p => ({ ...p, cover_image: `${API}/api/files/${res.data.path}` }));
            toast.success('Image uploaded');
        } catch { toast.error('Upload failed'); }
    };

    const addTag = () => {
        const t = tagInput.trim();
        if (t && !form.tags.includes(t)) {
            setForm(p => ({ ...p, tags: [...p.tags, t] }));
        }
        setTagInput('');
    };

    const toggleCity = (id) => setForm(p => ({ ...p, city_ids: p.city_ids.includes(id) ? p.city_ids.filter(c => c !== id) : [...p.city_ids, id] }));
    const toggleAttraction = (id) => setForm(p => ({ ...p, attraction_ids: p.attraction_ids.includes(id) ? p.attraction_ids.filter(a => a !== id) : [...p.attraction_ids, id] }));

    if (loading) return <DashboardLayout type="admin"><div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div></DashboardLayout>;

    // Editor View
    if (editing) return (
        <DashboardLayout type="admin">
            <div className="space-y-5" data-testid="blog-editor">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(null)} className="text-white/40 hover:text-white"><ArrowLeft className="w-4 h-4" /></Button>
                    <h1 className="text-xl font-bold text-white">{editing === 'new' ? 'New Blog Post' : 'Edit Blog Post'}</h1>
                    <div className="ml-auto flex gap-2">
                        <Button variant="outline" onClick={() => { setForm(p => ({ ...p, status: 'draft' })); handleSave(); }} disabled={saving} className="border-white/10 text-white/60 gap-2" data-testid="save-draft-btn">
                            <Save className="w-3.5 h-3.5" /> Save Draft
                        </Button>
                        <Button onClick={() => { setForm(p => ({ ...p, status: 'published' })); setTimeout(handleSave, 0); }} disabled={saving} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="publish-btn">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Publish
                        </Button>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-5">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-4">
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-5 space-y-4">
                                <div><Label className="text-white/50 text-xs">Title *</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value, slug: p.slug || e.target.value.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').trim() }))} className="bg-white/5 border-white/10 text-white text-lg" data-testid="post-title" /></div>
                                <div><Label className="text-white/50 text-xs">URL Slug</Label><Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} className="bg-white/5 border-white/10 text-white/60 text-sm font-mono" /></div>
                                <div><Label className="text-white/50 text-xs">Excerpt</Label><Textarea value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} className="bg-white/5 border-white/10 text-white min-h-[60px]" placeholder="Brief description for cards and SEO..." /></div>
                                <div><Label className="text-white/50 text-xs">Content (HTML)</Label><Textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="bg-white/5 border-white/10 text-white min-h-[350px] font-mono text-sm" placeholder="Write your article content here. HTML tags supported: <h2>, <h3>, <p>, <ul>, <li>, <strong>, <em>..." data-testid="post-content" /></div>
                            </CardContent>
                        </Card>

                        {/* SEO */}
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-5 space-y-3">
                                <h3 className="text-sm font-semibold text-white/60">SEO Settings</h3>
                                <div><Label className="text-white/50 text-xs">Meta Title</Label><Input value={form.meta_title} onChange={e => setForm(p => ({ ...p, meta_title: e.target.value }))} className="bg-white/5 border-white/10 text-white text-sm" placeholder="Custom title for search engines" /></div>
                                <div><Label className="text-white/50 text-xs">Meta Description</Label><Textarea value={form.meta_description} onChange={e => setForm(p => ({ ...p, meta_description: e.target.value }))} className="bg-white/5 border-white/10 text-white min-h-[60px] text-sm" placeholder="160 characters for Google snippet" /></div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-4">
                        {/* Cover Image */}
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-5 space-y-3">
                                <h3 className="text-sm font-semibold text-white/60">Cover Image</h3>
                                {form.cover_image && (
                                    <div className="relative rounded-lg overflow-hidden border border-white/10">
                                        <img src={form.cover_image} alt="Cover" className="w-full h-32 object-cover" />
                                        <button onClick={() => setForm(p => ({ ...p, cover_image: '' }))} className="absolute top-1.5 right-1.5 bg-black/70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">&times;</button>
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <label className="flex-1 cursor-pointer">
                                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                                        <div className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-white/15 bg-white/[0.03] hover:border-gold/30 text-xs text-white/50">
                                            <Upload className="w-3.5 h-3.5" /> Upload
                                        </div>
                                    </label>
                                    <Input value={form.cover_image?.startsWith(API) ? '' : (form.cover_image || '')} onChange={e => setForm(p => ({ ...p, cover_image: e.target.value }))} placeholder="Paste URL" className="bg-white/5 border-white/10 text-white text-xs flex-1" />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Category & Author */}
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-5 space-y-3">
                                <div><Label className="text-white/50 text-xs">Category</Label>
                                    <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                                        <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label className="text-white/50 text-xs">Author</Label><Input value={form.author_name} onChange={e => setForm(p => ({ ...p, author_name: e.target.value }))} className="bg-white/5 border-white/10 text-white text-sm mt-1" /></div>
                                <div className="flex items-center justify-between p-2 rounded bg-gold/5 border border-gold/10">
                                    <Label className="text-gold text-xs">Featured Post</Label>
                                    <Switch checked={form.is_featured} onCheckedChange={v => setForm(p => ({ ...p, is_featured: v }))} />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Tags */}
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-5 space-y-3">
                                <h3 className="text-sm font-semibold text-white/60">Tags</h3>
                                <div className="flex gap-2">
                                    <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add tag..." className="bg-white/5 border-white/10 text-white text-xs" />
                                    <Button variant="outline" size="sm" onClick={addTag} className="border-white/10 text-white/50 shrink-0"><Plus className="w-3 h-3" /></Button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(form.tags || []).map(tag => (
                                        <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/50 text-[10px] border border-white/10">
                                            {tag}
                                            <button onClick={() => setForm(p => ({ ...p, tags: p.tags.filter(t => t !== tag) }))} className="text-white/30 hover:text-white"><X className="w-2.5 h-2.5" /></button>
                                        </span>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Cities & Attractions */}
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-5 space-y-3">
                                <h3 className="text-sm font-semibold text-white/60">Linked Cities</h3>
                                <div className="flex flex-wrap gap-1.5">
                                    {cities.map(c => (
                                        <button key={c.id} onClick={() => toggleCity(c.id)} className={`px-2 py-1 rounded-full text-[10px] border transition-colors ${(form.city_ids || []).includes(c.id) ? 'bg-gold/15 text-gold border-gold/30' : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20'}`}>
                                            {c.name}
                                        </button>
                                    ))}
                                </div>
                                <h3 className="text-sm font-semibold text-white/60 pt-2">Linked Attractions</h3>
                                <div className="flex flex-wrap gap-1.5">
                                    {attractions.map(a => (
                                        <button key={a.id} onClick={() => toggleAttraction(a.id)} className={`px-2 py-1 rounded-full text-[10px] border transition-colors ${(form.attraction_ids || []).includes(a.id) ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20'}`}>
                                            {a.title}
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );

    // List View
    return (
        <DashboardLayout type="admin">
            <div className="space-y-6" data-testid="admin-blog">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Travel Insider</h1>
                        <p className="text-sm text-muted-foreground mt-1">Manage blog posts and AI-powered content</p>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={openCreate} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="new-post-btn">
                            <Plus className="w-4 h-4" /> New Post
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-white/10 pb-0">
                    {[{ key: 'posts', label: 'All Posts', icon: BookOpen }, { key: 'ai', label: 'AI Auto-Publisher', icon: Bot }].map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-[1px] ${tab === t.key ? 'border-gold text-gold' : 'border-transparent text-white/40 hover:text-white/60'}`}>
                            <t.icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </div>

                {/* AI Auto-Publisher Tab */}
                {tab === 'ai' && aiSettings && (
                    <div className="space-y-5">
                        {/* Status Card */}
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-5">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${aiSettings.enabled ? 'bg-emerald-500/15' : 'bg-white/5'}`}>
                                            <Bot className={`w-5 h-5 ${aiSettings.enabled ? 'text-emerald-400' : 'text-white/30'}`} />
                                        </div>
                                        <div>
                                            <h3 className="text-white font-semibold text-sm">AI Auto-Publisher</h3>
                                            <p className="text-xs text-white/30">{aiSettings.enabled ? 'Active — generating content automatically' : 'Paused — enable to start auto-publishing'}</p>
                                        </div>
                                    </div>
                                    <Switch checked={aiSettings.enabled} onCheckedChange={v => updateAiSetting({ enabled: v })} data-testid="ai-enabled-toggle" />
                                </div>
                                <div className="grid sm:grid-cols-3 gap-4 text-center">
                                    <div className="p-3 rounded-lg bg-white/[0.03]">
                                        <p className="text-2xl font-bold text-white">{aiSettings.posts_generated || 0}</p>
                                        <p className="text-[10px] text-white/30">Posts Generated</p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-white/[0.03]">
                                        <p className="text-sm font-medium text-white">{aiSettings.last_run ? new Date(aiSettings.last_run).toLocaleDateString() : 'Never'}</p>
                                        <p className="text-[10px] text-white/30">Last Generated</p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-white/[0.03]">
                                        <p className="text-sm font-medium text-white">{aiSettings.next_run ? new Date(aiSettings.next_run).toLocaleString() : 'Not scheduled'}</p>
                                        <p className="text-[10px] text-white/30">Next Scheduled</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Settings */}
                        <div className="grid lg:grid-cols-2 gap-5">
                            <Card className="bg-[#121620] border-white/10">
                                <CardContent className="p-5 space-y-4">
                                    <h3 className="text-sm font-semibold text-white">Schedule & Publishing</h3>
                                    <div>
                                        <Label className="text-white/50 text-xs mb-1.5 block">Publishing Frequency</Label>
                                        <Select value={String(aiSettings.frequency_hours)} onValueChange={v => updateAiSetting({ frequency_hours: parseInt(v) })}>
                                            <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="24">Daily (every 24 hours)</SelectItem>
                                                <SelectItem value="48">Every 2 days</SelectItem>
                                                <SelectItem value="72">Every 3 days</SelectItem>
                                                <SelectItem value="168">Weekly</SelectItem>
                                                <SelectItem value="336">Every 2 weeks</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
                                        <div>
                                            <Label className="text-white text-xs">Auto-Publish</Label>
                                            <p className="text-[10px] text-white/30">When OFF, posts are saved as drafts for your review</p>
                                        </div>
                                        <Switch checked={aiSettings.auto_publish} onCheckedChange={v => updateAiSetting({ auto_publish: v })} data-testid="auto-publish-toggle" />
                                    </div>
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
                                        <div>
                                            <Label className="text-white text-xs">Unsplash Cover Images</Label>
                                            <p className="text-[10px] text-white/30">Auto-fetch travel photos from Unsplash</p>
                                        </div>
                                        <Switch checked={aiSettings.use_unsplash} onCheckedChange={v => updateAiSetting({ use_unsplash: v })} />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-[#121620] border-white/10">
                                <CardContent className="p-5 space-y-4">
                                    <h3 className="text-sm font-semibold text-white">Content Types</h3>
                                    <p className="text-[10px] text-white/30">AI will rotate between enabled content types</p>
                                    <div className="space-y-2">
                                        {contentTypes.map(ct => {
                                            const enabled = (aiSettings.content_types || []).includes(ct.type);
                                            return (
                                                <div key={ct.type} className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                                                    <span className="text-xs text-white/70">{ct.label}</span>
                                                    <Switch checked={enabled} onCheckedChange={v => {
                                                        const types = aiSettings.content_types || [];
                                                        const next = v ? [...types, ct.type] : types.filter(t => t !== ct.type);
                                                        updateAiSetting({ content_types: next });
                                                    }} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Generate Now */}
                        <Card className="bg-gradient-to-r from-gold/[0.05] to-transparent border-gold/15">
                            <CardContent className="p-5">
                                <div className="flex items-center justify-between flex-wrap gap-4">
                                    <div className="flex items-center gap-3">
                                        <Sparkles className="w-5 h-5 text-gold" />
                                        <div>
                                            <h3 className="text-white font-semibold text-sm">Generate Now</h3>
                                            <p className="text-[10px] text-white/30">Manually trigger an AI blog post</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Select value={genType || '_random'} onValueChange={v => setGenType(v === '_random' ? '' : v)}>
                                            <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white text-xs"><SelectValue placeholder="Random type" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="_random">Random</SelectItem>
                                                {contentTypes.map(ct => <SelectItem key={ct.type} value={ct.type}>{ct.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <Button onClick={generateNow} disabled={generating} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="generate-now-btn">
                                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                            {generating ? 'Generating...' : 'Generate'}
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Posts Tab */}
                {tab === 'posts' && (
                    <>
                    <div className="flex gap-2">
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="w-[120px] bg-white/5 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Posts</SelectItem>
                                <SelectItem value="published">Published</SelectItem>
                                <SelectItem value="draft">Drafts</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>                {posts.length === 0 ? (
                    <div className="py-16 text-center text-white/20">
                        <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p>No blog posts yet</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {posts.map(post => (
                            <Card key={post.id} className="bg-[#121620] border-white/10 hover:border-white/15 transition-colors" data-testid={`post-row-${post.id}`}>
                                <CardContent className="p-0">
                                    <div className="flex items-center gap-4 p-4">
                                        {/* Thumbnail */}
                                        <div className="w-20 h-14 rounded-lg overflow-hidden bg-white/5 shrink-0">
                                            {post.cover_image ? <img src={post.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-5 h-5 text-white/10" /></div>}
                                        </div>
                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-sm font-medium text-white truncate">{post.title}</h3>
                                                {post.is_featured && <span className="text-[9px] text-gold bg-gold/10 px-1.5 py-0.5 rounded-full">Featured</span>}
                                            </div>
                                            <div className="flex items-center gap-3 text-[10px] text-white/30">
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${post.status === 'published' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/5 text-white/40'}`}>{post.status}</span>
                                                <span>{post.category}</span>
                                                <span>{post.views || 0} views</span>
                                                {post.published_at && <span>{new Date(post.published_at).toLocaleDateString()}</span>}
                                            </div>
                                        </div>
                                        {/* Actions */}
                                        <div className="flex items-center gap-1 shrink-0">
                                            {post.status === 'published' && (
                                                <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-white/5 text-white/30 hover:text-white"><ExternalLink className="w-3.5 h-3.5" /></a>
                                            )}
                                            <button onClick={() => openEdit(post)} className="p-1.5 rounded hover:bg-white/5 text-white/30 hover:text-white" data-testid={`edit-post-${post.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                            <button onClick={() => handleDelete(post.id)} className="p-1.5 rounded hover:bg-red-500/10 text-white/30 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
                </>
                )}
            </div>
        </DashboardLayout>
    );
}
